/**
 * QUIPWA — DriveService
 *
 * Domain-level operations over Google Drive API v3, built on `DriveHttp`
 * (auth, typed errors, retries). This class knows Drive's REST shapes; it does
 * not know about decks, notes or sync — those live in their own modules.
 *
 * Layout in the user's Drive:
 *
 *   My Drive/
 *   └── QUIPWA/                 ← root folder (visible, the user's data)
 *       ├── *.wflash            ← decks
 *       ├── Quipwa_Notes/       ← notes (.md), course sub-folders
 *       ├── Quipwa_Data/        ← legacy notes_index.json
 *       ├── assets/             ← theme backgrounds, celebration images
 *       └── _state.db           ← legacy location of the replicated state
 *
 *   appDataFolder (hidden)      ← _state.db and app metadata (see sync module)
 *
 * Contract: methods throw `DriveError`. `null` is returned **only** when Drive
 * answered successfully that nothing matches — never on a failed request.
 * See docs/modulos/drive.md.
 */

import { getRootFolderId, setRootFolderId } from '../auth/local-storage.ts'
import { DriveHttp, type DriveHttpOptions } from './drive-http.ts'
import { DriveError, isDriveError } from './drive-errors.ts'
import { q } from './drive-query.ts'
import { defaultBlobStore, type IdbBlobStore } from '../db/idb-blob-store.ts'

export const DRIVE_API = 'https://www.googleapis.com/drive/v3'
export const UPLOAD_API = 'https://www.googleapis.com/upload/drive/v3'
export const FOLDER_MIME = 'application/vnd.google-apps.folder'
export const ROOT_FOLDER_NAME = 'QUIPWA'
export const NOTES_FOLDER_NAME = 'Quipwa_Notes'
export const DATA_FOLDER_NAME = 'Quipwa_Data'
export const ASSETS_FOLDER_NAME = 'assets'
/** Special alias for the hidden per-app folder (requires the drive.appdata scope). */
export const APP_DATA_FOLDER = 'appDataFolder'

/** Uploads above this size use a resumable session instead of a single request. */
export const RESUMABLE_THRESHOLD_BYTES = 5 * 1024 * 1024

/** Metadata fields requested for every file. Partial responses keep payloads small. */
export const FILE_FIELDS = 'id,name,mimeType,modifiedTime,size,version,md5Checksum,parents,appProperties,trashed'

// ─── TYPES ───────────────────────────────────────────────────────────────────

export interface DriveFile {
  id: string
  name: string
  mimeType: string
  /** RFC 3339 timestamp set by Drive (server clock). */
  modifiedTime: string
  /** Bytes, as a decimal string. Absent for folders and Google Docs. */
  size?: string
  /**
   * Monotonically increasing version, bumped by every change on the server.
   * The sync engine compares it instead of timestamps, so device clocks never
   * matter. Decimal string (int64).
   */
  version?: string
  /** MD5 of the content, computed by Drive. Absent for folders. */
  md5Checksum?: string
  parents?: string[]
  /** Private key/value metadata only this app can see. Values ≤ 124 bytes. */
  appProperties?: Record<string, string>
  trashed?: boolean
}

export interface CreateFileInput {
  name: string
  parentId: string
  mimeType: string
  content: ArrayBuffer | Uint8Array | string | Blob
  appProperties?: Record<string, string>
  /** Pre-generated id (from `generateIds`), so offline-created files keep a stable id. */
  id?: string
}

export interface UpdateFileOptions {
  appProperties?: Record<string, string>
  name?: string
}

type Body = ArrayBuffer | Uint8Array | string | Blob

function toBlob(content: Body, mimeType: string): Blob {
  if (content instanceof Blob) return content
  if (typeof content === 'string') return new Blob([content], { type: mimeType })
  // Copy into a fresh ArrayBuffer-backed view: Blob rejects SharedArrayBuffer views.
  const bytes = content instanceof Uint8Array ? content : new Uint8Array(content)
  return new Blob([new Uint8Array(bytes)], { type: mimeType })
}

/** Build a `multipart/related` body: JSON metadata part + media part. */
function multipartBody(metadata: object, content: Body, mimeType: string): { body: Blob; contentType: string } {
  const boundary = `quipwa-${crypto.randomUUID()}`
  const media = toBlob(content, mimeType)
  const body = new Blob([
    `--${boundary}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n`,
    JSON.stringify(metadata),
    `\r\n--${boundary}\r\nContent-Type: ${mimeType}\r\n\r\n`,
    media,
    `\r\n--${boundary}--`,
  ])
  return { body, contentType: `multipart/related; boundary=${boundary}` }
}

function byteLength(content: Body): number {
  if (content instanceof Blob) return content.size
  if (typeof content === 'string') return new TextEncoder().encode(content).byteLength
  return content.byteLength
}

// ─── SERVICE ─────────────────────────────────────────────────────────────────

/** Where downloaded assets are kept on the device (IdbBlobStore in the app). */
export type AssetStore = Pick<IdbBlobStore, 'get' | 'put'>

const assetKey = (fileId: string) => `asset:${fileId}`

export class DriveService {
  readonly http: DriveHttp
  /** In-flight/settled folder lookups, so concurrent callers don't race to create the same folder. */
  private readonly folderCache = new Map<string, Promise<string>>()
  private readonly assets: AssetStore | null

  /** @param assets device cache for assets; without one every read downloads */
  constructor(http: DriveHttp, assets: AssetStore | null = null) {
    this.http = http
    this.assets = assets
  }

  // ── Metadata & listing ───────────────────────────────────────────────────

  async getFileMeta(fileId: string): Promise<DriveFile> {
    const params = new URLSearchParams({ fields: FILE_FIELDS })
    return this.http.json<DriveFile>(`${DRIVE_API}/files/${encodeURIComponent(fileId)}?${params}`, {
      label: `files.get ${fileId}`,
    })
  }

  /** Like `getFileMeta`, but `null` when the file does not exist (404). */
  async findById(fileId: string): Promise<DriveFile | null> {
    try {
      return await this.getFileMeta(fileId)
    } catch (err) {
      if (isDriveError(err, 'not_found')) return null
      throw err
    }
  }

  /**
   * List every file matching `query`, following `nextPageToken` until the end.
   * Drive returns at most `pageSize` files per page (100 by default), so a
   * single request silently truncated folders with more than 100 notes.
   */
  async listAll(query: string, options: { spaces?: string; orderBy?: string } = {}): Promise<DriveFile[]> {
    const files: DriveFile[] = []
    let pageToken: string | undefined
    do {
      const params = new URLSearchParams({
        q: query,
        fields: `nextPageToken,files(${FILE_FIELDS})`,
        pageSize: '1000',
      })
      if (options.spaces) params.set('spaces', options.spaces)
      if (options.orderBy) params.set('orderBy', options.orderBy)
      if (pageToken) params.set('pageToken', pageToken)

      const page = await this.http.json<{ files?: DriveFile[]; nextPageToken?: string }>(
        `${DRIVE_API}/files?${params}`,
        { label: 'files.list' }
      )
      files.push(...(page.files ?? []))
      pageToken = page.nextPageToken
    } while (pageToken)
    return files
  }

  /** Direct, non-trashed children of a folder, optionally filtered by MIME type. */
  async listChildren(parentId: string, mimeType?: string): Promise<DriveFile[]> {
    const isAppData = parentId === APP_DATA_FOLDER
    return this.listAll(
      q.and(q.inParent(parentId), q.notTrashed(), mimeType && q.mimeTypeIs(mimeType)),
      isAppData ? { spaces: APP_DATA_FOLDER } : { orderBy: 'modifiedTime desc' }
    )
  }

  /**
   * Find a child by exact name. Returns `null` only when Drive confirms there is
   * no such file; any failed request throws. When duplicates exist (an earlier
   * bug could create them), the most recently modified one wins.
   */
  async findFile(name: string, parentId: string, mimeType?: string): Promise<DriveFile | null> {
    const isAppData = parentId === APP_DATA_FOLDER
    const matches = await this.listAll(
      q.and(q.nameEquals(name), q.inParent(parentId), q.notTrashed(), mimeType && q.mimeTypeIs(mimeType)),
      isAppData ? { spaces: APP_DATA_FOLDER } : {}
    )
    if (matches.length === 0) return null
    return matches.reduce((newest, f) => (f.modifiedTime > newest.modifiedTime ? f : newest))
  }

  // ── Content ──────────────────────────────────────────────────────────────

  async downloadFile(fileId: string): Promise<ArrayBuffer> {
    return this.http.arrayBuffer(`${DRIVE_API}/files/${encodeURIComponent(fileId)}?alt=media`, {
      label: `files.download ${fileId}`,
    })
  }

  async downloadText(fileId: string): Promise<string> {
    return new TextDecoder().decode(await this.downloadFile(fileId))
  }

  async downloadJSON<T>(fileId: string): Promise<T> {
    return JSON.parse(await this.downloadText(fileId)) as T
  }

  /** Create a file with content in one request (resumable above 5 MiB). */
  async createFile(input: CreateFileInput): Promise<DriveFile> {
    const metadata: Record<string, unknown> = {
      name: input.name,
      mimeType: input.mimeType,
      parents: [input.parentId],
    }
    if (input.appProperties) metadata['appProperties'] = input.appProperties
    if (input.id) metadata['id'] = input.id

    if (byteLength(input.content) > RESUMABLE_THRESHOLD_BYTES) {
      return this.resumableUpload('POST', `${UPLOAD_API}/files`, metadata, input.content, input.mimeType)
    }

    const { body, contentType } = multipartBody(metadata, input.content, input.mimeType)
    const params = new URLSearchParams({ uploadType: 'multipart', fields: FILE_FIELDS })
    return this.http.json<DriveFile>(`${UPLOAD_API}/files?${params}`, {
      method: 'POST',
      headers: { 'Content-Type': contentType },
      body,
      label: `files.create ${input.name}`,
      // A retried create after a lost response would duplicate the file —
      // unless the id was pre-generated, which makes the retry idempotent.
      retry: Boolean(input.id),
    })
  }

  /** Replace a file's content (and optionally metadata). */
  async updateFileContent(
    fileId: string,
    content: Body,
    mimeType: string,
    options: UpdateFileOptions = {}
  ): Promise<DriveFile> {
    const metadata: Record<string, unknown> = {}
    if (options.appProperties) metadata['appProperties'] = options.appProperties
    if (options.name) metadata['name'] = options.name
    const url = `${UPLOAD_API}/files/${encodeURIComponent(fileId)}`

    if (byteLength(content) > RESUMABLE_THRESHOLD_BYTES) {
      return this.resumableUpload('PATCH', url, metadata, content, mimeType)
    }

    if (Object.keys(metadata).length === 0) {
      const params = new URLSearchParams({ uploadType: 'media', fields: FILE_FIELDS })
      return this.http.json<DriveFile>(`${url}?${params}`, {
        method: 'PATCH',
        headers: { 'Content-Type': mimeType },
        body: toBlob(content, mimeType),
        label: `files.update ${fileId}`,
      })
    }

    const { body, contentType } = multipartBody(metadata, content, mimeType)
    const params = new URLSearchParams({ uploadType: 'multipart', fields: FILE_FIELDS })
    return this.http.json<DriveFile>(`${url}?${params}`, {
      method: 'PATCH',
      headers: { 'Content-Type': contentType },
      body,
      label: `files.update ${fileId}`,
    })
  }

  /** Change metadata only (name, appProperties, trashed…). */
  async updateMetadata(fileId: string, patch: Record<string, unknown>): Promise<DriveFile> {
    const params = new URLSearchParams({ fields: FILE_FIELDS })
    return this.http.json<DriveFile>(`${DRIVE_API}/files/${encodeURIComponent(fileId)}?${params}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
      label: `files.updateMetadata ${fileId}`,
    })
  }

  /**
   * Move a file to Drive's trash. Recoverable for 30 days from drive.google.com,
   * which is why the app trashes instead of deleting permanently.
   * A file that is already gone counts as done.
   */
  async trashFile(fileId: string): Promise<void> {
    try {
      await this.updateMetadata(fileId, { trashed: true })
    } catch (err) {
      if (!isDriveError(err, 'not_found')) throw err
    }
  }

  /**
   * Move a file into another folder, optionally renaming it. The id is kept, so
   * anything pointing at it — [[links]], embeds, the notes index — still resolves.
   */
  async moveFile(fileId: string, newParentId: string, newName?: string): Promise<DriveFile> {
    const current = await this.http.json<{ parents?: string[] }>(
      `${DRIVE_API}/files/${encodeURIComponent(fileId)}?fields=parents`,
      { label: `files.get ${fileId}` }
    )
    const params = new URLSearchParams({ addParents: newParentId, fields: FILE_FIELDS })
    const stale = (current.parents ?? []).filter((p) => p !== newParentId)
    if (stale.length) params.set('removeParents', stale.join(','))

    return this.http.json<DriveFile>(`${DRIVE_API}/files/${encodeURIComponent(fileId)}?${params}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(newName ? { name: newName } : {}),
      label: `files.move ${fileId}`,
    })
  }

  /**
   * Reserve file ids before the files exist. Lets the app create notes offline
   * with their final Drive id, so routes and [[links]] never have to be rewritten.
   */
  async generateIds(count: number, space: 'drive' | 'appDataFolder' = 'drive'): Promise<string[]> {
    const params = new URLSearchParams({ count: String(count), space, type: 'files' })
    const res = await this.http.json<{ ids: string[] }>(`${DRIVE_API}/files/generateIds?${params}`, {
      label: 'files.generateIds',
    })
    return res.ids
  }

  // ── Folders ──────────────────────────────────────────────────────────────

  async createFolder(name: string, parentId: string, id?: string): Promise<DriveFile> {
    const body: Record<string, unknown> = { name, mimeType: FOLDER_MIME, parents: [parentId] }
    if (id) body['id'] = id
    const params = new URLSearchParams({ fields: FILE_FIELDS })
    return this.http.json<DriveFile>(`${DRIVE_API}/files?${params}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      label: `folders.create ${name}`,
      retry: Boolean(id),
    })
  }

  /**
   * Return the id of the named sub-folder, creating it only when Drive confirms
   * it does not exist. Concurrent calls share one lookup.
   */
  ensureFolder(name: string, parentId: string): Promise<string> {
    const key = `${parentId}/${name}`
    const cached = this.folderCache.get(key)
    if (cached) return cached

    const lookup = (async () => {
      const existing = await this.findFile(name, parentId, FOLDER_MIME)
      if (existing) return existing.id
      return (await this.createFolder(name, parentId)).id
    })()
    // Never cache a failure: the next call must be able to retry.
    lookup.catch(() => this.folderCache.delete(key))
    this.folderCache.set(key, lookup)
    return lookup
  }

  /** The `QUIPWA` folder in My Drive. Cached in localStorage across sessions. */
  async ensureRootFolder(): Promise<string> {
    const cached = getRootFolderId()
    if (cached) return cached
    const id = await this.ensureFolder(ROOT_FOLDER_NAME, 'root')
    setRootFolderId(id)
    return id
  }

  async ensureNotesFolder(): Promise<string> {
    return this.ensureFolder(NOTES_FOLDER_NAME, await this.ensureRootFolder())
  }

  async ensureDataFolder(): Promise<string> {
    return this.ensureFolder(DATA_FOLDER_NAME, await this.ensureRootFolder())
  }

  async ensureAssetsFolder(): Promise<string> {
    return this.ensureFolder(ASSETS_FOLDER_NAME, await this.ensureRootFolder())
  }

  /** Forget cached folder ids (on sign-out, or when a cached id turns out stale). */
  resetFolderCache(): void {
    this.folderCache.clear()
  }

  // ── Convenience wrappers used across the app ─────────────────────────────

  async listWFlashFiles(): Promise<DriveFile[]> {
    const rootId = await this.ensureRootFolder()
    return (await this.listChildren(rootId)).filter((f) => f.name.endsWith('.wflash'))
  }

  /**
   * Upload a theme/celebration image to `QUIPWA/assets/`. Returns its file id.
   * The bytes go straight into the device cache: showing it never downloads.
   */
  async uploadAsset(name: string, content: Blob | ArrayBuffer | string, mimeType: string): Promise<string> {
    const parentId = await this.ensureAssetsFolder()
    const id = (await this.createFile({ name, parentId, mimeType, content })).id
    if (this.assets && typeof content !== 'string') {
      const bytes = content instanceof Blob ? await content.arrayBuffer() : content
      await this.assets.put(assetKey(id), bytes).catch(() => { /* quota or private mode: it downloads later */ })
    }
    return id
  }

  async uploadJSON<T>(name: string, data: T, parentId: string): Promise<DriveFile> {
    return this.createFile({ name, parentId, mimeType: 'application/json', content: JSON.stringify(data, null, 2) })
  }

  async updateJSON<T>(fileId: string, data: T): Promise<DriveFile> {
    return this.updateFileContent(fileId, JSON.stringify(data, null, 2), 'application/json')
  }

  /** An asset as an object URL. The caller owns (and must revoke) it. */
  async getAssetUrl(fileId: string): Promise<string> {
    return URL.createObjectURL(new Blob([await this.getAssetBytes(fileId)]))
  }

  /**
   * An asset's bytes, from the device cache when possible.
   *
   * Assets never change once uploaded: every upload creates a new file, so a
   * cached copy is valid for as long as the id is in use. Before, the
   * background image (up to 5 MB) was downloaded again on every start (audit
   * D11), and images in notes could not be shown offline. The cache lives in
   * the same IndexedDB database as the SQLite files, so erasing the device
   * clears it too.
   */
  async getAssetBytes(fileId: string): Promise<ArrayBuffer> {
    const cached = await this.assets?.get(assetKey(fileId)).catch(() => null)
    if (cached) return cached
    const bytes = await this.downloadFile(fileId)
    await this.assets?.put(assetKey(fileId), bytes).catch(() => { /* quota or private mode: still shown */ })
    return bytes
  }

  // ── internals ─────────────────────────────────────────────────────────────

  /** Single-chunk resumable upload: open a session, then PUT the whole body. */
  private async resumableUpload(
    method: 'POST' | 'PATCH',
    url: string,
    metadata: Record<string, unknown>,
    content: Body,
    mimeType: string
  ): Promise<DriveFile> {
    const media = toBlob(content, mimeType)
    const params = new URLSearchParams({ uploadType: 'resumable', fields: FILE_FIELDS })
    const session = await this.http.request(`${url}?${params}`, {
      method,
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Upload-Content-Type': mimeType,
        'X-Upload-Content-Length': String(media.size),
      },
      body: JSON.stringify(metadata),
      label: 'upload.session',
    })
    const location = session.headers.get('Location')
    if (!location) throw new DriveError('unknown', 'Drive upload.session → la respuesta no incluye Location')

    return this.http.json<DriveFile>(location, {
      method: 'PUT',
      headers: { 'Content-Type': mimeType },
      body: media,
      label: 'upload.put',
    })
  }
}

// ─── DEFAULT INSTANCE ────────────────────────────────────────────────────────

/**
 * What the default instance needs from the auth layer. The auth module plugs
 * itself in with `connectDriveAuth` at startup; until then every request fails
 * as `unauthorized`. Keeping the dependency pointed this way (auth → drive)
 * means the Drive layer can be imported — and tested — without a browser.
 */
export interface DriveAuthBridge {
  getAccessToken: () => Promise<string | null>
  onUnauthorized?: (() => void) | undefined
  onInsufficientScope?: (() => void) | undefined
}

const authBridge: DriveAuthBridge = { getAccessToken: async () => null }

export function connectDriveAuth(bridge: DriveAuthBridge): void {
  authBridge.getAccessToken = bridge.getAccessToken
  authBridge.onUnauthorized = bridge.onUnauthorized
  authBridge.onInsufficientScope = bridge.onInsufficientScope
}

/** Build a DriveService; by default wired to the auth bridge above and the device's asset cache. */
export function createDriveService(overrides: Partial<DriveHttpOptions> = {}, assets: AssetStore | null = defaultBlobStore): DriveService {
  return new DriveService(
    new DriveHttp({
      getAccessToken: () => authBridge.getAccessToken(),
      onUnauthorized: () => authBridge.onUnauthorized?.(),
      onInsufficientScope: () => authBridge.onInsufficientScope?.(),
      ...overrides,
    }),
    assets
  )
}

export const driveService = createDriveService()
